import {
  BoxGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
} from "three";

import type { RenderVector3 } from "../types.ts";

export interface ThreeCrossesLandmark {
  readonly object: Group;
  dispose(): void;
}

/**
 * Low-detail silhouette interpretation of the Three Crosses monument.
 *
 * The authored proportions intentionally claim no survey accuracy. The pinned
 * M4 reference supports only the silhouette structure: three separated shafts,
 * taller centre, broad crossbars, angular supports, and a low winged base.
 */
export function createThreeCrossesLandmark(
  position: Readonly<RenderVector3>,
): ThreeCrossesLandmark {
  const object = new Group();
  object.name = "landmark:three-crosses";
  object.position.set(position.x, position.y, position.z);
  // The source manifest establishes the silhouette, not a surveyed azimuth.
  // Present its broad cross plane toward the checked-in Old Town vista.
  object.rotation.y = Math.PI / 2;
  object.userData = {
    referenceManifest: "data/overrides/m4/three-crosses-silhouette.json",
    proportions: "authored-silhouette-not-surveyed",
    orientation: "authored-facing-old-town-not-surveyed",
  };
  const concrete = new MeshStandardMaterial({
    color: 0xeee7dc,
    emissive: 0x34322e,
    emissiveIntensity: 0.16,
    roughness: 0.92,
    metalness: 0,
  });

  const addBox = (
    name: string,
    size: Readonly<RenderVector3>,
    offset: Readonly<RenderVector3>,
    rotationZ = 0,
  ): void => {
    const mesh = new Mesh(
      new BoxGeometry(size.x, size.y, size.z),
      concrete,
    );
    mesh.name = `${object.name}:${name}`;
    mesh.position.set(offset.x, offset.y, offset.z);
    mesh.rotation.z = rotationZ;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    object.add(mesh);
  };

  // The monument rises from a compact terraced concrete platform. Keeping the
  // tiers broad and shallow gives the distant M4 vista the supported, winged
  // silhouette visible in the pinned reference without asserting surveyed
  // dimensions.
  addBox(
    "lower-terrace",
    { x: 19.2, y: 0.48, z: 6.2 },
    { x: 0, y: 0.24, z: 0 },
  );
  addBox(
    "middle-terrace",
    { x: 17.8, y: 0.54, z: 5.3 },
    { x: 0, y: 0.75, z: 0 },
  );
  addBox(
    "low-winged-base",
    { x: 16.4, y: 0.72, z: 4.4 },
    { x: 0, y: 1.38, z: 0 },
  );
  for (const footX of [-5.2, 0, 5.2] as const) {
    addBox(
      `cross-foot-${footX}`,
      { x: 3.5, y: 1.05, z: 3.4 },
      { x: footX, y: 2.24, z: 0 },
    );
  }
  const shaftBaseElevationM = 2.76;
  for (const cross of [
    { id: "left", x: -5.2, shaftHeight: 11.5, crossbarY: 9.1 },
    { id: "centre", x: 0, shaftHeight: 15.5, crossbarY: 12.2 },
    { id: "right", x: 5.2, shaftHeight: 11.5, crossbarY: 9.1 },
  ] as const) {
    addBox(
      `${cross.id}-shaft`,
      { x: 1.55, y: cross.shaftHeight, z: 1.5 },
      {
        x: cross.x,
        y: shaftBaseElevationM + cross.shaftHeight / 2,
        z: 0,
      },
    );
    addBox(
      `${cross.id}-crossbar`,
      { x: cross.id === "centre" ? 6.6 : 5.7, y: 1.45, z: 1.5 },
      { x: cross.x, y: shaftBaseElevationM + cross.crossbarY, z: 0 },
    );
  }
  addBox(
    "left-angular-support",
    { x: 1.15, y: 8.2, z: 1.5 },
    { x: -3.4, y: 6.4, z: 0 },
    -0.52,
  );
  addBox(
    "right-angular-support",
    { x: 1.15, y: 8.2, z: 1.5 },
    { x: 3.4, y: 6.4, z: 0 },
    0.52,
  );

  return {
    object,
    dispose(): void {
      object.traverse((child) => {
        if (child instanceof Mesh) {
          child.geometry.dispose();
        }
      });
      concrete.dispose();
      object.removeFromParent();
      object.clear();
    },
  };
}
